import React, { useEffect, useState } from "react";
import "./eachactioncomponent.css";
import { processAction } from "./actionProcessor";
import { useReadMemory } from "../../../hooks/useReadMemory";
import MemoryType from "../../../../../service/memory/enum/MemoryType";
import FormService from "../../../../../components/generic/formbuilder/networkservice/FormService";
import { FormOutlined } from "@ant-design/icons";
import { Popconfirm, Popover, Spin, Tooltip, notification } from "antd";
import { useRecoilValue } from "recoil";
import { currentConfiguration } from "../../../../../service/memory/recoil/configurationstore";
import ConfigurationConstants from "../../../../utils/configurationconstants";
import { useNavigate } from "react-router-dom";
import { RoleToLinkMapper } from "../../../../utils/UrlUtils";
import { useGetRole } from "../../../hooks/useGetRole";
import { TextField } from "@mui/material";
import { convertTimeToTimestamp } from "../../../../../utils/dateutils";
import CustomModal from "../../../../../components/generic/custommodal/CustomModal";
import SecondaryButton from "../../../../../components/generic/buttons/SecondaryButton";
import PrimaryButton from "../../../../../components/generic/buttons/PrimaryButton";
import EditDeliveryComponent from "../../../../carecoordinator/requestforvolunteeringservice/components/EditDeliveryComponent";
import { notifiyUser } from "../../../../utils/Notification";
import { strings } from "../../../../utils/strings";
import { publishEvent } from "../../../../../components/generic/EventPublisher";
import { useGetOrgIdentifier } from "../../../hooks/useGetOrgIdentifier";
import SmartIcon from "../../../../../components/generic/genericIcon/SmartIcon";

const EachActionComponent = ({ action, actionData, actionType, showIconsOnly }) => {
  const userJson = useReadMemory(MemoryType.USER);
  const formService = new FormService();
  const configuration = useRecoilValue(currentConfiguration);
  const organizationIdentifier = useGetOrgIdentifier();
  const navigate = useNavigate();
  const role = useGetRole();
  const [commentInput, setCommentInput] = useState('');
  const [allForms, setAllForms] = useState(undefined);
  const [isPopupOverContentLoading, setIsPopupOverContentLoading] = useState(undefined);
  const [secretKey, setSecretKey] = useState(undefined);
  const [openEditVisitModal, setOpenEditVisitModal] = useState(false);
  const [modalSubmitButtonVisibility, setModalSubmitButtonVisibility] = useState(false);
  const [editDeliveryDetails, setEditDeliveryDetails] = useState({});

  useEffect(() => {
    if (configuration !== undefined && secretKey === undefined) {
      const key = configuration[ConfigurationConstants.NIRAMAYA_ORGANIZATION_SECRETKEY];
      setSecretKey(key?.key)
    }
  }, [configuration]);

  const handleFormView = async (templateCode) => {
    const form = actionData?.formIdentifier && JSON.parse(actionData?.formIdentifier).find((eachFormObject) => eachFormObject?.templateCode === templateCode);
    navigate(
      `${RoleToLinkMapper(role)}form-viewer?templateCode=${templateCode}&deliveryIdentifier=${actionData?.deliveryIdentifier}&patientIdentifier=${actionData?.patientIdentifier
      }&workforceIdentifier=${actionData?.workforceIdentifier}&deliveryStatus=${actionData?.deliveryStatus}&formIdentifier=${form?.formIdentifier}&opportunityIdentifier=${actionData?.opportunityIdentifier}`
    );

    if (form?.formIdentifier) {
      const formData = {
        givenName: actionData?.patientGivenName,
        familyName: actionData?.patientFamilyName,
        ...form
      }
    }
  }

  const handleSubmitForm = async () => {
    if (actionData?.templateCode) {
      navigate(
        `${RoleToLinkMapper(role)}form-viewer?templateCode=${actionData?.templateCode}&deliveryIdentifier=${actionData?.deliveryIdentifier}&workforceIdentifier=${actionData?.workforceIdentifier}&formIdentifier=${actionData?.formIdentifier}&opportunityIdentifier=${actionData?.opportunityIdentifier}`
      );
      if (actionData?.formIdentifier) {
        const formData = {
          givenName: actionData?.patientGivenName,
          familyName: actionData?.patientFamilyName,
          ...actionData
        }
      }
    } else {
      handleFormView(actionData?.templateCodes?.[0]);
    }
  }

  const getForms = async () => {
    let formsList = [];
    const forms = await formService.getAllFormMetaData(organizationIdentifier);
    if (forms.data) {
      for (const form of forms.data) {
        formsList.push(form);
      }
    }
    setAllForms(formsList);
  };

  const getFormsAndSetState = async () => {
    if (allForms === undefined) {
      getForms();
    }
  }

  const setFormsPopoverContent = () => {
    getFormsAndSetState();
    const forms = [];
    if (actionData?.templateCodes !== undefined && allForms !== undefined) {
      for (const template of actionData?.templateCodes) {
        const form = allForms.find(formItem => formItem.templateCode === template);
        if (form) {
          forms.push({ templateCode: form.templateCode, title: form.title });
        }
      }
    }
    if (forms.length > 0) {
      setIsPopupOverContentLoading(false);
    }

    if (forms.length > 1) {
      return (
        <div className="actioncard-popover-content">
          {isPopupOverContentLoading === undefined || isPopupOverContentLoading ? (
          <Spin size="medium" className="d-flex justify-content-center m-auto" />
        ) : <>
          {forms.map(form => (
            <div className="card-secondary-action-btn-2" onClick={() => handleFormView(form.templateCode)}><FormOutlined />&nbsp;&nbsp;{form.title}</div>
          ))}
        </>}
      </div>
    )
    }
  }

  const handleCommentInput = (e) => {
    setCommentInput(e.target.value);
  }

  const handleActionClick = async (actionToBePerformed, data, user) => {
    let result;
    let message = "";
    switch (actionToBePerformed.functionName) {
      case "acceptOpportunity":
        result = await processAction(actionToBePerformed, data, user)
        notifiyUser(result, strings.OPPORTUNITY_ACCEPTED_SUCCESS, strings.OPPORTUNITY_ACCEPTED_ERROR);
        break;
      case "rejectOpportunity":
        result = await processAction(actionToBePerformed, data, user)
        notifiyUser(result, strings.OPPORTUNITY_DECLINED_SUCCESS, strings.OPPORTUNITY_DECLINED_ERROR);
        break;
      case "updateDeliveryStatus":
        result = await processAction(actionToBePerformed, data, user)
        if (actionToBePerformed?.to === "Started") {
          notifiyUser(result, strings.VISIT_STARTED_SUCCESS, strings.VISIT_STARTED_ERROR);
        } else if (actionToBePerformed?.to === "Completed") {
          notifiyUser(result, strings.FORM_SUBMITTED_SUCCESS, strings.FORM_SUBMITTED_ERROR);
        } else {
          notifiyUser(result, strings.VISIT_DETAILS_UPDATED_SUCCESS, strings.VISIT_DETAILS_UPDATED_ERROR);
        }
        setEditDeliveryDetails({});
        break;
      case "editDeliveryForm":
        result = await processAction(actionToBePerformed, data, user)
        notifiyUser(result, strings.VISIT_DETAILS_UPDATED_SUCCESS, strings.VISIT_DETAILS_UPDATED_ERROR);
        setEditDeliveryDetails({});
        break;
      case "approveDelivery":
        result = await processAction(actionToBePerformed, data, user);
        notifiyUser(result, strings.VISIT_APPROVED_SUCCESS, strings.VISIT_APPROVED_ERROR);
        break;
      case "addDelivery":
        result = await processAction(actionToBePerformed, data, user);
        notifiyUser(result, strings.CREATE_VISIT_SUCCESS, strings.CREATE_VISIT_ERROR);
        setEditDeliveryDetails({});
        break;
      case "rejectDelivery":
        const newComment = {
          comment: commentInput, 
          identifier: user?.userIdentifier, 
          fullName: user?.givenName + user?.familyName, 
          time: convertTimeToTimestamp(new Date())
        };
        const commentsToBeModified = JSON.parse(data?.comments ?? "[]")
        commentsToBeModified?.push(newComment);
        data["comments"] = commentsToBeModified;
        result = await processAction(actionToBePerformed, data, user);
        notifiyUser(result, strings.VISIT_REJECTED_SUCCESS, strings.VISIT_REJECTED_ERROR);
        break;
      case "navigateToMaps":
        user = { ...user, secretKey: secretKey };
        result = await processAction(actionToBePerformed, data, user);
        if (result[0]?.latitude && result[0]?.longitude) {
          const googleMapUrl = "https://maps.google.com?q=" + result[0]?.latitude + "," + result[0]?.longitude
          window.open(googleMapUrl);
          notifiyUser(result, strings.NAVIGATE_TO_MAPS_SUCCESS, strings.NAVIGATE_TO_MAPS_ERROR);
        } else {
          result = new Error();
        }
        break;
      case "editDeliveryData":
        setOpenEditVisitModal(true);
        break;
      case "updateDeliveryDetails":
        result = await processAction(actionToBePerformed, data, user);
        notifiyUser(result, strings.VISIT_DETAILS_UPDATED_SUCCESS, strings.VISIT_DETAILS_UPDATED_ERROR);
        break;
      case "viewAuditLogs":
        if (data?.[`${data?.serviceType}Identifier`]) {
          navigate(RoleToLinkMapper(role) + "audit-logs?visit=" + data?.[`${data?.serviceType}Identifier`]);
        } else {
          notification.error({
            message: "Something went wrong",
            placement: "bottomRight",
          });
        }
        break;
      default:
        break;
    }
    if (result instanceof Error) {
      notification.error({
        message: `Something went wrong`,
        placement: "bottomRight",
      });
    } else {
      if (message !== "") {
        notification.success({
          message: message,
          placement: "bottomRight",
        });
      }
    }
  }

  const onDataChangeCallback = async (data) => {
    setEditDeliveryDetails(data);
  }

  const onSubmitOfDeliveryDetailsAfterEdit = () => {
    handleActionClick({ ...action, functionName: "updateDeliveryDetails" }, { ...actionData, ...editDeliveryDetails }, userJson);
    setOpenEditVisitModal(false);
  }

  const onCancelButtonClicked = () => {
    setOpenEditVisitModal(false);
  }

  const customEditVisitModalButtons = (
    <div className='button-section'>
      <SecondaryButton buttonText="Cancel" handleOnClick={onCancelButtonClicked} />
      <PrimaryButton buttonText="Submit" handleOnClick={onSubmitOfDeliveryDetailsAfterEdit} buttonDisable={modalSubmitButtonVisibility} />
    </div>
  )

  const actionMapper = {
    ACCEPT_OPPORTUNITY: <div className={`card-${actionType}-action-btn-1`} onClick={() => {handleActionClick(action, actionData, userJson)}}>{action.label}</div>,
    REJECT_OPPORTUNITY: <div className={`card-${actionType}-reject-btn`} onClick={() => {handleActionClick(action, actionData, userJson)}}>{action.label}</div>,
    START: <div className={`card-${actionType}-action-btn-1`} onClick={() => {handleActionClick(action, actionData, userJson)}}>{action.label}</div>,
    COMPLETE: <div className={`card-${actionType}-action-btn-1`} onClick={() => {handleActionClick(action, { ...actionData, comments: "" }, userJson)}}>{action.label}</div>,
    CREATE_VISIT: <div className={`card-${actionType}-action-btn-1`} onClick={() => {handleActionClick(action, actionData, userJson)}}>{action.label}</div>,
    SUBMIT_FORM: actionData?.templateCodes?.length > 1 ? <Popover arrowPointAtCenter={true} placement="bottom" content={setFormsPopoverContent} overlayClassName="actioncard-popover hide-arrow"><div className={`card-${actionType}-action-btn-1`}>{actionData?.formIdentifier ? "View " : "Fill "}{action.label}</div></Popover> : <div className={`card-${actionType}-action-btn-1`} onClick={handleSubmitForm}>{actionData?.formIdentifier ? "View " : "Fill "}{action.label}</div>,
    VIEW_FORM: actionData?.templateCodes?.length > 1 ? <Popover arrowPointAtCenter={true} placement="bottom" content={setFormsPopoverContent} overlayClassName="actioncard-popover hide-arrow"><div className={`card-${actionType}-action-btn-1`}>View {action.label} </div></Popover> : <div className={`card-${actionType}-action-btn-1`} onClick={handleSubmitForm}>View {action.label} </div>,
    APPROVE: <div className={`card-${actionType}-action-btn-1`} onClick={() => { handleActionClick(action, actionData, userJson) }}>{action.label}</div>,
    // EDIT_VISIT: <div><div className={`card-${actionType}-action-btn-2`} onClick={() => {handleActionClick(action, actionData, userJson)}}>{action.label}</div></div>,
    EDIT_VISIT:<div className={`card-${actionType}-action-btn-2`} onClick={() => {handleActionClick(action, actionData, userJson)}}>{action.label}</div>,
    TRANSFER_VISIT: <div><div className={`card-${actionType}-action-btn-2`} onClick={() => {handleActionClick(action, actionData, userJson)}}>{action.label}</div></div>,
    OPEN_MAPS: <div onClick={() => { handleActionClick(action, actionData, userJson) }}>
      {showIconsOnly ? <div className="action-icons"><SmartIcon iconKey={"location"} height="16" width="16" /></div> : <div className={`card-${actionType}-action-btn-2`}>{action.label}</div>}
    </div>,
    SEND_MESSAGE: <div onClick={() => { handleActionClick(action, actionData, userJson) }}>
      {showIconsOnly ? <div className="action-icons"><SmartIcon iconKey={"message"} height="16" width="16" /></div> : <div className={`card-${actionType}-action-btn-2`}>{action.label}</div>}
    </div>,
    CALL_USER: <div onClick={() => { handleActionClick(action, actionData, userJson) }}>
      {showIconsOnly ? <div className="action-icons"><SmartIcon iconKey={"call"} height="16" width="16" /></div> : <div className={`card-${actionType}-action-btn-2`}>{action.label}</div>}
    </div>,
    COMMENT: <div><div className={`card-${actionType}-action-btn-2`} onClick={() => {handleActionClick(action, actionData, userJson)}}>{action.label}</div></div>,
    AUDIT_LOGS:<div className={`card-${actionType}-action-btn-2`} onClick={() => {handleActionClick(action, actionData, userJson)}}>{action.label}</div>,
    REJECT: (
      <Popconfirm
        title={
          <div>
            <p>Please enter a reason for rejection</p>
            <TextField 
              label="Reject Reason"
              multiline
              maxRows={5} value={commentInput} onChange={handleCommentInput}
              sx={{
                "& .MuiOutlinedInput-root": {
                    color: "#000",
                    "&.Mui-focused": {
                        "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "var(--main-theme-color) !important",
                        },
                    },
                } ,  
                "& .MuiInputLabel-outlined": {
                    "&.Mui-focused": {
                        color: "var(--main-theme-color) !important",
                    },
                },
            }}/>
          </div>
        }
        placement="top"
        onConfirm={() => {handleActionClick(action, actionData, userJson)}}
        okText="Confirm"
        cancelText="Cancel"
        onCancel={() => setCommentInput("")}
      >
        <div className={`card-${actionType}-reject-btn`}>{action.label}</div>
      </Popconfirm>
    ),
    EDIT_FORM: actionData?.templateCodes?.length > 1 ? <Popover arrowPointAtCenter={true} placement="bottom" content={setFormsPopoverContent} overlayClassName="actioncard-popover"><div className={`card-${actionType}-action-btn-1`}><FormOutlined />&nbsp;&nbsp;Edit {action.label}</div></Popover> : <div className={`card-${actionType}-action-btn-1`} onClick={handleSubmitForm}><FormOutlined />&nbsp;&nbsp;Edit {action.label}</div>,
    ICON_PREFERENCE: <div onClick={action.functionName}>
      {showIconsOnly ?<Tooltip title="Set Preferences"><div className="action-icons"><SmartIcon iconKey={"settings"} height="16" width="16" /></div></Tooltip> : <div className={`card-${actionType}-action-btn-2`}>{action.label}</div>}
    </div>,
    ICON_FOR_PROFILE_EDIT: <div onClick={action.functionName}>
      {showIconsOnly ?<Tooltip title="Edit Profile Details"><div className="action-icons"><SmartIcon iconKey={"edit"} height="16" width="16" /></div></Tooltip> : <div className={`card-${actionType}-action-btn-2`}>{action.label}</div>}
    </div>,
    REQUEST_FOR_VOLUNTEER_SERVICE: <div onClick={action.functionName}>
      {showIconsOnly ? <Tooltip placement="topRight" title="Request for Volunteer Service"><><SmartIcon iconKey={"volunteerRequest"} height="20" width="20" /></></Tooltip> : <div className={`card-${actionType}-action-btn-2`}>{action.label}</div>}
    </div>,
    ARTICLE_DELETE_ICON: (
      <Tooltip placement="topRight" title="Delete">
        <Popconfirm
          title="Are you sure to delete this entry?"
          placement="left"
          onConfirm={() => action.functionName(actionData)}
          okText="Confirm"
          cancelText="Cancel"
          overlayClassName='article-delete-popconfirm'
        >
          <div className="action-icons"><SmartIcon iconKey={"delete"} height="20" width="20" /></div>
        </Popconfirm>
      </Tooltip>
    ),
    ARTICLE_EDIT_ICON: <div onClick={() => action.functionName(actionData)}>
      <Tooltip placement="topRight" title="Update"><><SmartIcon iconKey={"edit"} height="20" width="20" /></></Tooltip>
    </div>,
    ICON_FOR_RESET_PIN: (
      <Tooltip placement="topRight" title="Reset Pin">
        <Popconfirm
          title="Are you sure to reset the pin?"
          placement="left"
          onConfirm={() => action.functionName(actionData)}
          okText="Confirm"
          cancelText="Cancel"
          overlayClassName='article-delete-popconfirm'
        >
          <div className="action-icons"><SmartIcon iconKey={"reset_pin"} height="16" width="16" /></div>
        </Popconfirm>
      </Tooltip>
    ),
  };

  return (
    <>
      {actionMapper[action.map]}

      {openEditVisitModal && (
        <CustomModal isModalOpen={openEditVisitModal} footer={[customEditVisitModalButtons]} closeModal={setOpenEditVisitModal}>
          <EditDeliveryComponent initialDeliveryData={actionData} onDataChangeCallback={onDataChangeCallback} setSubmitButtonVisibility={setModalSubmitButtonVisibility} />
        </CustomModal>
      )}
    </>
  )
};

export default EachActionComponent;
